import React from 'react';
import { calculateChartScale, ScaleConfig } from '@/utils/chartScaling';

interface BarData {
  label: string;
  value: number;
}

interface BarChartProps {
  data: BarData[];
  className?: string;
  unit?: 'units' | 'percent' | 'persons';
  scaleConfig?: ScaleConfig;
  yAxisLabel?: string;
  barSpacing?: 'normal' | 'compact';
}

export const snapshot_BarChart: React.FC<BarChartProps> = ({ 
  data, 
  className = "", 
  unit = "units",
  scaleConfig = {
    padding: 0.1,
    tickCount: 5,
    includeZero: true
  },
  yAxisLabel,
  barSpacing = 'normal'
}) => {
  // Chart dimensions
  const CHART_HEIGHT = 300;
  const MIN_BAR_HEIGHT = 1;
  const AXIS_WIDTH = 80;
  const Y_AXIS_LABEL_WIDTH = 18;
  const BOTTOM_PADDING = 32;
  const BAR_WIDTH = 40; // Fixed bar width as per requirement
  const MIN_GAP = 8; // Minimum gap between bars
  const MAX_GAP = 120; // Increased maximum gap to allow for better space utilization

  console.log('Bar Chart Data:', data);

  // Format values for display
  const formatValue = (value: number, isAxisLabel: boolean = false): string => {
    if (unit === 'percent') {
      return `${value.toFixed(1)}%`;
    }
    const formattedNumber = new Intl.NumberFormat().format(value);
    if (isAxisLabel) {
      return formattedNumber;
    }
    return unit === 'persons' ? `${formattedNumber} people` : `${formattedNumber} ${unit}`;
  };

  // Calculate scale using our utility
  const values = data.map(d => d.value);
  const scale = calculateChartScale(values, {
    ...scaleConfig,
    dataType: unit === 'percent' ? 'percentage' : 'unit',
    customFormatter: (value) => formatValue(value, true),
    includeZero: true,
    // Enforce min zero for both percentage and unit data since negative values don't make sense
    enforceMinZero: scaleConfig.enforceMinZero || unit === 'units'
  });

  // Calculate the zero position in the chart space (from top)
  const zeroY = ((scale.max) / (scale.max - scale.min)) * CHART_HEIGHT;

  // Calculate bar dimensions
  const calculateBarDimensions = (value: number): { height: number; y: number } => {
    if (scale.max === scale.min) return { height: MIN_BAR_HEIGHT, y: zeroY };

    const range = scale.max - scale.min;
    const valueHeight = Math.abs(value) / range * CHART_HEIGHT;
    const height = Math.max(MIN_BAR_HEIGHT, valueHeight);

    if (value >= 0) {
      // Positive values: grow up from zero line
      return {
        height,
        y: zeroY - height // Position above zero line
      };
    } else {
      // Negative values: grow down from zero line
      return {
        height,
        y: zeroY // Position below zero line
      };
    }
  };

  // Generate y-axis labels and positions
  const yAxisPositions = scale.ticks.map((tick, index) => {
    const normalizedValue = (scale.max - tick.value) / (scale.max - scale.min);
    return normalizedValue * CHART_HEIGHT;
  });

  // Calculate dynamic gap based on container width and number of bars
  const useDynamicGap = () => {
    const [containerWidth, setContainerWidth] = React.useState(0);
    const [effectiveWidth, setEffectiveWidth] = React.useState(0);
    const containerRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
      const updateWidth = () => {
        if (containerRef.current) {
          setContainerWidth(containerRef.current.clientWidth);
          setEffectiveWidth(containerRef.current.clientWidth);
        }
      };

      // Initial measurement
      updateWidth();

      // Setup resize observer
      const resizeObserver = new ResizeObserver(updateWidth);
      if (containerRef.current) {
        resizeObserver.observe(containerRef.current);
      }

      return () => resizeObserver.disconnect();
    }, []);

    // Calculate the optimal gap
    const calculateGap = () => {
      if (!effectiveWidth || !data.length) return MIN_GAP;

      const totalBarWidth = BAR_WIDTH * data.length;
      let availableSpace = effectiveWidth - totalBarWidth;

      // For 5-bar charts, we need to account for edge padding
      if (data.length === 5) {
        // We want one gap unit on the edges (split into half on each side)
        // So we need (data.length - 1) gaps between bars + 1 gap for edges
        const totalGapUnits = (data.length - 1) + 1;
        const optimalGap = Math.floor(availableSpace / totalGapUnits);
        return Math.max(MIN_GAP, Math.min(MAX_GAP, optimalGap));
      }

      // Default behavior for other charts
      const gaps = data.length - 1;
      if (gaps <= 0) return MIN_GAP;

      const optimalGap = Math.floor(availableSpace / gaps);
      return Math.max(MIN_GAP, Math.min(MAX_GAP, optimalGap));
    };

    const gap = calculateGap();
    
    // Calculate edge padding - only for 5-bar charts
    const edgePadding = data.length === 5 ? `${gap / 2}px` : '0px';

    return {
      ref: containerRef,
      gap,
      edgePadding
    };
  };

  const { ref: containerRef, gap, edgePadding } = useDynamicGap();

  return (
    <div className={`bg-white relative w-full text-base text-[#212121] font-semibold whitespace-nowrap uppercase tracking-[0.32px] leading-[1.3] mt-10 pb-6 max-md:max-w-full font-geist-mono ${className}`}>
      {/* Y-axis label */}
      {yAxisLabel && (
        <div 
          className="absolute left-4 top-1/2 -translate-y-1/2 text-xs text-[#666666] font-normal leading-tight align-middle chart-text-shift"
          style={{
            width: Y_AXIS_LABEL_WIDTH,
            transform: 'rotate(-90deg) translateX(-50%)',
            transformOrigin: '0 50%',
            whiteSpace: 'nowrap',
            textAlign: 'center',
            lineHeight: '1.2',
            verticalAlign: 'middle'
          }}
        >
          {yAxisLabel}
        </div>
      )}
      
      {/* Y-axis labels */}
      <div 
        className="absolute top-0 h-full w-[70px] flex flex-col justify-between pb-[30px]"
        style={{ left: yAxisLabel ? `${Y_AXIS_LABEL_WIDTH}px` : '0' }}
      >
        {scale.ticks.map((tick, index) => (
          <div 
            key={index} 
            className="absolute text-right pr-4 text-xs text-[#666666] font-normal leading-tight align-middle chart-text-shift"
            style={{
              top: `${yAxisPositions[index]}px`,
              right: 0,
              transform: 'translateY(-50%)',
              width: '100%',
              lineHeight: '1.2',
              verticalAlign: 'middle'
            }}
          >
            {tick.label}
          </div>
        ))}
      </div>
      
      {/* Chart area */}
      <div 
        ref={containerRef}
        className={`h-[300px] relative mb-8`}
        style={{ 
          marginLeft: yAxisLabel ? `${AXIS_WIDTH + Y_AXIS_LABEL_WIDTH}px` : `${AXIS_WIDTH}px`
        }}
      >
        {/* Grid lines */}
        <div className="absolute inset-0 pointer-events-none">
          {scale.ticks.map((tick, index) => (
            <div
              key={`grid-line-${index}`}
              className={`absolute w-full border-b ${tick.value === 0 ? 'border-black/20 border-solid' : 'border-dotted border-black/10'}`}
              style={{ 
                top: `${yAxisPositions[index]}px`
              }}
            />
          ))}
        </div>

        {/* Bars container */}
        <div 
          className="absolute inset-0 flex justify-between"
          style={{
            gap: `${gap}px`,
            paddingLeft: edgePadding,
            paddingRight: edgePadding
          }}
        >
          {/* Bars */}
          {data.map((bar, index) => {
            const { height, y } = calculateBarDimensions(bar.value);
            const isNegative = bar.value < 0;

            return (
              <div
                key={index}
                className="flex flex-col items-center flex-shrink-0"
                style={{ 
                  width: `${BAR_WIDTH}px`
                }}
              >
                <div 
                  className={`w-full relative group transition-all duration-200 ${isNegative ? 'rounded-[0px_0px_10px_10px]' : 'rounded-[10px_10px_0px_0px]'}`}
                  style={{ 
                    height: `${height}px`,
                    transform: `translateY(${y}px)`,
                    background: 'linear-gradient(180deg, rgba(202, 0, 121, 0.50) 0%, rgba(243, 186, 211, 0.50) 100%)'
                  }}
                >
                  {/* Tooltip */}
                  <div className="opacity-0 group-hover:opacity-100 absolute left-1/2 -translate-x-1/2 bg-black text-white text-xs py-1 px-2 rounded whitespace-nowrap z-10"
                    style={{
                      bottom: isNegative ? 'auto' : '100%',
                      top: isNegative ? '100%' : 'auto',
                      marginBottom: isNegative ? '0' : '4px',
                      marginTop: isNegative ? '4px' : '0'
                    }}
                  >
                    <span className="text-xs font-medium leading-tight align-middle chart-text-shift" style={{lineHeight: '1.2', verticalAlign: 'middle'}}>
                      Year: {bar.label} <br />
                    </span>
                    <span className="text-xs font-medium leading-tight align-middle chart-text-shift" style={{lineHeight: '1.2', verticalAlign: 'middle'}}>
                      Change in {unit}: {formatValue(bar.value)}
                    </span>
                  </div>
                </div>
                <div 
                  className="text-center text-xs text-[#666666] font-normal absolute -rotate-45 sm:rotate-0 origin-top-left"
                  style={{
                    bottom: '-24px',
                    width: '100%'
                  }}
                >
                  {bar.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};